import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  Tooltip,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';

const DEFAULT_PRESET_HOURS = [
  '7:30-16:30',
  '9:00-17:00',
  '16:30-00:30',
];

const PresetHoursSettings = ({ initialHours, onChange }) => {
  const presetHours = initialHours || DEFAULT_PRESET_HOURS;
  const [newHour, setNewHour] = useState('');
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleAddHour = () => {
    const trimmed = newHour.trim();
    if (!trimmed) {
      setError('Εισάγετε ωράριο');
      return;
    }
    if (presetHours.includes(trimmed)) {
      setError('Αυτό το ωράριο υπάρχει ήδη');
      return;
    }
    const updated = [...presetHours, trimmed];
    setNewHour('');
    setError('');
    if (onChange) onChange(updated);
  };

  const handleRemoveHour = (hour) => {
    const updated = presetHours.filter((h) => h !== hour);
    if (onChange) onChange(updated);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAddHour();
  };

  const formatTimeRange = (hour) => {
    const parts = hour.split('-');
    if (parts.length === 2) {
      return { start: parts[0].trim(), end: parts[1].trim() };
    }
    return null;
  };

  return (
    <Paper sx={{
      borderRadius: 3, overflow: 'hidden',
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      border: '1px solid rgba(0,0,0,0.06)',
    }}>
      {/* Header */}
      <Box sx={{
        p: { xs: 2, md: 3 },
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        bgcolor: 'rgba(0,0,0,0.01)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AccessTimeIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
              Προκαθορισμένες Ώρες Βάρδιας
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {presetHours.length} {presetHours.length === 1 ? 'ωράριο' : 'ωράρια'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Add input */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
          <TextField
            placeholder="π.χ. 10:00-18:00"
            value={newHour}
            onChange={(e) => { setNewHour(e.target.value); setError(''); }}
            onKeyDown={handleKeyDown}
            error={!!error}
            helperText={error}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccessTimeIcon sx={{ fontSize: 18, color: '#999' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8b5cf6' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8b5cf6' },
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddHour}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              minWidth: isMobile ? 'auto' : 130, px: isMobile ? 2 : 3,
              bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' },
              boxShadow: '0 4px 14px rgba(139,92,246,0.3)',
            }}
          >
            {isMobile ? '' : 'Προσθήκη'}
          </Button>
        </Box>

        {/* Preset hours cards */}
        {presetHours.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Δεν υπάρχουν προκαθορισμένες ώρες. Προσθέστε μία παραπάνω.
          </Alert>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 1.5,
          }}>
            {presetHours.map((hour) => {
              const range = formatTimeRange(hour);
              return (
                <Box key={hour} sx={{
                  p: 2, borderRadius: 2,
                  border: '1px solid rgba(139,92,246,0.15)',
                  bgcolor: 'rgba(139,92,246,0.03)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(139,92,246,0.06)',
                    borderColor: 'rgba(139,92,246,0.3)',
                    boxShadow: '0 2px 8px rgba(139,92,246,0.1)',
                  },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <AccessTimeIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                    {range ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                          {range.start} - {range.end}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{hour}</Typography>
                    )}
                  </Box>
                  <Tooltip title="Διαγραφή">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveHour(hour)}
                      sx={{
                        color: '#c4b5fd',
                        '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default PresetHoursSettings;
